import ElementGroups from "@ext/markdown/core/element/ElementGroups";
import { GALLERY_MAX_ITEMS } from "@ext/markdown/elements/gallery/model/gallery";
import { Fragment, type Node } from "prosemirror-model";
import { NodeSelection, type Transaction } from "prosemirror-state";

const isGallery = (node: Node | null) => node?.type.name === "gallery";

export const findNodePos = (doc: Node, target: Node) => {
	let found: number | null = null;
	doc.descendants((node, pos) => {
		if (found !== null) return false;
		if (node === target) found = pos;
	});
	return found;
};

export const canAddToGallery = (doc: Node, pos: number) => {
	const node = doc.nodeAt(pos);
	if (!node || !doc.type.schema.nodes.gallery) return false;
	return isGallery(node) || node.type.isInGroup(ElementGroups.galleryItem);
};

const selectNode = (tr: Transaction, node: Node) => {
	const pos = findNodePos(tr.doc, node);
	if (pos !== null) tr.setSelection(NodeSelection.create(tr.doc, pos));
	return tr;
};

const insertIntoGallery = (tr: Transaction, galleryPos: number, index: number, items: Node[]) => {
	const gallery = tr.doc.nodeAt(galleryPos);
	const room = Math.max(GALLERY_MAX_ITEMS - gallery.childCount, 0);
	const overflow = items.slice(room);

	if (overflow.length) tr.insert(galleryPos + gallery.nodeSize, Fragment.from(overflow));

	let insertPos = galleryPos + 1;
	for (let child = 0; child < index; child++) insertPos += gallery.child(child).nodeSize;
	tr.insert(insertPos, Fragment.from(items.slice(0, room)));
	return tr;
};

export const addToGallery = (tr: Transaction, targetPos: number, items: Node[]) => {
	const target = tr.doc.nodeAt(targetPos);
	const Target = tr.doc.resolve(targetPos);

	if (isGallery(target)) insertIntoGallery(tr, targetPos, target.childCount, items);
	else if (isGallery(Target.parent)) insertIntoGallery(tr, Target.before(), Target.index() + 1, items);
	else {
		const inside = items.slice(0, GALLERY_MAX_ITEMS - 1);
		const gallery = tr.doc.type.schema.nodes.gallery.create(null, [target, ...inside]);
		tr.replaceWith(targetPos, targetPos + target.nodeSize, [gallery, ...items.slice(inside.length)]);
	}

	return selectNode(tr, items.at(-1) ?? target);
};

export const wrapInGallery = (tr: Transaction, pos: number) => addToGallery(tr, pos, []);
